import React from 'react';
import { Download } from 'lucide-react';
import Modal from '../../../../components/Modal';
import { Button, Select } from '../../../../components';
import { useDutyRoster } from '../DutyRosterContext';

const ExportPayrollModal = () => {
    const { dailyDate, exportModalOpen, setExportModalOpen, exportPeriod, setExportPeriod, exportEmployeeId, setExportEmployeeId, fmtUKDate, fmtHours, exportEmployeeOptions, exportDateRange, exportLoading, buildPayroll, downloadPayrollCsv } = useDutyRoster();
    return (
            <Modal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                title="Export payroll hours"
                maxWidth="max-w-4xl"
                footer={(() => {
                    const { rows } = buildPayroll();
                    return (
                        <div className="flex items-center justify-between gap-3">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setExportModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={downloadPayrollCsv}
                                disabled={rows.length === 0 || exportLoading}
                                loading={exportLoading}
                                leftIcon={<Download className="w-4 h-4" />}
                            >
                                {exportLoading ? 'Loading…' : `Download ${rows.length} payroll row${rows.length === 1 ? '' : 's'} CSV`}
                            </Button>
                        </div>
                    );
                })()}
            >
                {(() => {
                    const { rows, recordsChecked, reviewCount, notIncluded, totalPayableHours } = buildPayroll();
                    const range = exportDateRange();
                    const PERIOD_LABELS = { day: 'Selected day', week: 'This week', fourweek: '4-week payroll period', month: 'Calendar month' };
                    const periodLabel = PERIOD_LABELS[exportPeriod] || 'Selected day';
                    const dateLabel = range.length > 1
                        ? `${fmtUKDate(range[0])} – ${fmtUKDate(range[range.length - 1])}`
                        : fmtUKDate(range[0] || dailyDate);
                    const employeeLabel = exportEmployeeId === 'all'
                        ? 'All employees'
                        : (exportEmployeeOptions.find((e) => String(e.id) === String(exportEmployeeId))?.name || 'Employee');
                    return (
                        <div className="space-y-4 pt-1">
                            {/* Sub-title + format badge */}
                            <div className="flex items-start justify-between gap-4">
                                <p className="text-[12px] text-[#64748B] dark:text-gray-400">
                                    Payroll-ready timesheet data · {periodLabel} · {dateLabel} · {employeeLabel}
                                </p>
                                <span className="shrink-0 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-[11px] font-bold">
                                    CSV · UK 24-hour time
                                </span>
                            </div>
                            <p className="text-[12px] text-[#64748B] dark:text-gray-400 leading-relaxed">
                                Exports matched or manager-approved actual hours. Unpaid breaks are deducted
                                automatically. Live shifts, future shifts and records needing review are not
                                included in payroll.
                            </p>

                            {/* Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c]">
                                <div>
                                    <label className="block text-[10px] font-extrabold uppercase tracking-wide text-[#64748B] dark:text-gray-400 mb-1.5">Export period</label>
                                    <Select
                                        className="w-full"
                                        value={exportPeriod}
                                        onChange={setExportPeriod}
                                        options={[
                                            { value: 'day', label: 'Selected day' },
                                            { value: 'week', label: 'This week' },
                                            { value: 'fourweek', label: '4-week payroll period' },
                                            { value: 'month', label: 'Calendar month' },
                                        ]}
                                    />
                                    {exportLoading && (
                                        <p className="mt-1 text-[11px] font-semibold text-blue-600">Loading payroll data for this period…</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-[10px] font-extrabold uppercase tracking-wide text-[#64748B] dark:text-gray-400 mb-1.5">Employee</label>
                                    <Select
                                        className="w-full"
                                        value={exportEmployeeId}
                                        onChange={setExportEmployeeId}
                                        options={[{ value: 'all', label: 'All employees' }, ...exportEmployeeOptions.map((e) => ({ value: String(e.id), label: e.name }))]}
                                    />
                                </div>
                                <p className="md:col-span-2 -mt-1 text-[11px] text-[#94A3B8] dark:text-gray-500">
                                    Exports payroll-ready actual hours for the chosen period. Includes every employee with records in this period.
                                </p>
                            </div>

                            {/* Summary cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: 'Records checked', value: recordsChecked, tone: 'text-[#1E293B] dark:text-gray-200' },
                                    { label: 'Payroll-ready rows', value: rows.length, tone: 'text-[#1E293B] dark:text-gray-200' },
                                    { label: 'Total payable hours', value: fmtHours(totalPayableHours), tone: 'text-emerald-600 dark:text-emerald-400' },
                                    { label: `Not included · ${reviewCount} review`, value: notIncluded, tone: 'text-amber-600 dark:text-amber-400' },
                                ].map((c) => (
                                    <div key={c.label} className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#31373f] px-3.5 py-3">
                                        <div className="text-[9px] font-extrabold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500 mb-1.5">{c.label}</div>
                                        <div className={`text-[22px] font-bold leading-none ${c.tone}`}>{c.value}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Preview table — payroll-ready rows only */}
                            <div className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 overflow-hidden">
                                <div className="overflow-x-auto max-h-[320px]">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-[#F8FAFC] dark:bg-[#20242c] sticky top-0">
                                            <tr className="text-[9px] font-extrabold uppercase tracking-wide text-[#64748B] dark:text-gray-400">
                                                <th className="px-4 py-3">Employee</th>
                                                <th className="px-4 py-3">Team</th>
                                                <th className="px-4 py-3">Work date</th>
                                                <th className="px-4 py-3">Pay code</th>
                                                <th className="px-4 py-3">Clock in</th>
                                                <th className="px-4 py-3">Clock out</th>
                                                <th className="px-4 py-3">Unpaid break</th>
                                                <th className="px-4 py-3">Payable hours</th>
                                                <th className="px-4 py-3">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="px-4 py-10 text-center text-[13px] text-[#94A3B8] dark:text-gray-500">
                                                        No payroll-ready rows for these filters.
                                                    </td>
                                                </tr>
                                            ) : rows.map((r, i) => (
                                                <tr key={`${r.ref}-${r.date.toISOString()}-${i}`} className="border-t border-[#EDF1F6] dark:border-gray-800 text-[12px] text-[#334155] dark:text-gray-200">
                                                    <td className="px-4 py-3">
                                                        <div className="font-semibold text-[#1E293B] dark:text-gray-200">{r.name}</div>
                                                        <div className="text-[10px] text-[#94A3B8] dark:text-gray-500">{r.ref}</div>
                                                    </td>
                                                    <td className="px-4 py-3">{r.team}</td>
                                                    <td className="px-4 py-3">{fmtUKDate(r.date)}</td>
                                                    <td className="px-4 py-3 font-medium">{r.payCode}</td>
                                                    <td className="px-4 py-3">{r.clockIn}</td>
                                                    <td className="px-4 py-3">{r.clockOut}</td>
                                                    <td className="px-4 py-3">{r.breakMins}m</td>
                                                    <td className="px-4 py-3 font-bold">{fmtHours(r.payableHours)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wide bg-emerald-100 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400">{r.status}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 leading-relaxed">
                                <b className="text-[#64748B] dark:text-gray-400">Simple payroll CSV:</b> employee reference, employee name, team,
                                work date, pay code, clock in, clock out, unpaid break minutes, payable hours and status.
                                It can be opened in Excel or mapped into most payroll systems. Tax, National Insurance and
                                pension calculations remain in the payroll software.
                            </p>
                        </div>
                    );
                })()}
            </Modal>
    );
};

export default ExportPayrollModal;
